<admin-app-header></admin-app-header>
<app-prospecting-nav></app-prospecting-nav>

<div class="scraper-page">
    <div class="tabs-bar">
        <button [class.active]="activeTab === 'quick'" (click)="activeTab = 'quick'"><i class="fa-solid fa-magnifying-glass"></i> Simple Google Maps Search</button>
        <button [class.active]="activeTab === 'jobs'" (click)="activeTab = 'jobs'"><i class="fa-solid fa-layer-group"></i> Advanced Google Maps Search</button>
    </div>

    <div class="alert alert--ok" *ngIf="message">{{ message }}</div>
    <div class="alert alert--err" *ngIf="error">{{ error }}</div>

    <!-- Simple Google Maps Search -->
    <div *ngIf="activeTab === 'quick'" class="grid">
        <div class="card">
            <h2><i class="fa-solid fa-magnifying-glass-location"></i> Simple Google Maps Search</h2>
            <p class="hint">Pick an area and type what you're looking for. This runs a single Apify scrape job.</p>

            <div class="loading-state" *ngIf="loadingConfig"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</div>

            <ng-container *ngIf="!loadingConfig">
                <label class="field-label">Area</label>
                <select [(ngModel)]="selectedArea" class="full">
                    <option value="">Choose an area...</option>
                    <optgroup label="Cape Town">
                        <option *ngFor="let a of areas" [value]="a.slug" [hidden]="a.region !== 'Cape Town'">{{ a.name }}</option>
                    </optgroup>
                    <optgroup label="Johannesburg">
                        <option *ngFor="let a of areas" [value]="a.slug" [hidden]="a.region !== 'Johannesburg'">{{ a.name }}</option>
                    </optgroup>
                </select>

                <label class="field-label">Search term</label>
                <input type="text" class="full" [(ngModel)]="searchTerm" placeholder="e.g. head office, business park, office, corporate office" />
                <p class="hint" style="margin-top: 4px">Type anything — this is what Google Maps will search for in the selected area.</p>

                <label class="field-label">Max results per search</label>
                <input type="number" class="full" [(ngModel)]="maxResults" min="1" max="9999" placeholder="50" />

                <div class="cost-row">
                    <span>1 job &middot; up to {{ cappedMax | number }} places</span>
                    <strong>~${{ estimatedCost | number: '1.2-2' }}</strong>
                </div>

                <button class="run-btn" [disabled]="starting" (click)="runScrape()">
                    <i class="fa-solid" [class.fa-spinner]="starting" [class.fa-spin]="starting" [class.fa-play]="!starting"></i>
                    {{ starting ? 'Starting...' : 'Run scrape' }}
                </button>
            </ng-container>
        </div>

        <div class="card">
            <h2><i class="fa-solid fa-gauge-high"></i> API accounts</h2>
            <button class="secondary-btn" (click)="loadStatus()" [disabled]="loadingStatus">
                <i class="fa-solid" [class.fa-spinner]="loadingStatus" [class.fa-spin]="loadingStatus" [class.fa-rotate]="!loadingStatus"></i>
                Refresh
            </button>

            <div class="status-block" *ngIf="status?.apify">
                <h3>Apify</h3>
                <div *ngIf="!status.apify.error">
                    <div class="status-row"><span>Account</span><strong>{{ status.apify.username || '-' }}</strong></div>
                    <div class="status-row"><span>Plan</span><strong>{{ status.apify.plan || '-' }}</strong></div>
                    <div class="status-row"><span>Monthly usage</span><strong>${{ status.apify.monthlyUsageUsd || 0 | number: '1.2-2' }}</strong></div>
                </div>
                <div class="status-err" *ngIf="status.apify.error">{{ status.apify.error }}</div>
            </div>

            <div class="status-block" *ngIf="status?.hunter">
                <h3>Hunter.io</h3>
                <div *ngIf="!status.hunter.error">
                    <div class="status-row"><span>Plan</span><strong>{{ status.hunter.planName || '-' }}</strong></div>
                    <div class="status-row"><span>Searches</span><strong>{{ status.hunter.searchesUsed || 0 }} / {{ status.hunter.searchesAvailable || 0 }}</strong></div>
                    <div class="status-row"><span>Verifications</span><strong>{{ status.hunter.verificationsUsed || 0 }} / {{ status.hunter.verificationsAvailable || 0 }}</strong></div>
                </div>
                <div class="status-err" *ngIf="status.hunter.error">{{ status.hunter.error }}</div>
            </div>
        </div>
    </div>

    <!-- Advanced Google Maps Search -->
    <div *ngIf="activeTab === 'jobs'">
        <div class="card">
            <h2><i class="fa-solid fa-layer-group"></i> Advanced Google Maps Search</h2>
            <p class="hint">Configure search terms and regions for bulk scraping. Each enabled term is combined with each region to create scrape jobs.</p>
        </div>

        <!-- Search Terms -->
        <div class="card">
            <h2><i class="fa-solid fa-tags"></i> Search Terms</h2>
            <div class="add-row">
                <input type="text" placeholder="Name (e.g. Business Parks)" [(ngModel)]="newTermName" />
                <input type="text" placeholder="Search query (e.g. business park)" [(ngModel)]="newTermQuery" />
                <button class="run-btn" (click)="addConfiguredTerm()" [disabled]="!newTermName.trim()"><i class="fa-solid fa-plus"></i> Add</button>
            </div>
            <div class="term-list">
                <div *ngFor="let term of configuredTerms" class="term-item" [class.disabled]="!term.enabled">
                    <label class="toggle-label">
                        <input type="checkbox" [checked]="term.enabled" (change)="toggleTerm(term)" />
                        <strong>{{ term.name }}</strong>
                        <span class="term-query">"{{ term.searchQuery }}"</span>
                    </label>
                    <button class="icon-btn danger" (click)="deleteTerm(term)" title="Delete"><i class="fa-solid fa-trash"></i></button>
                </div>
                <div class="empty-state" *ngIf="!configuredTerms.length">
                    No search terms configured yet. Add one above.
                </div>
            </div>
        </div>

        <!-- Areas -->
        <div class="card">
            <h2><i class="fa-solid fa-map-pin"></i> Areas / Regions</h2>
            <p class="hint">Suburbs grouped by region. The scraper builds a polygon from each region's points.</p>
            <div *ngFor="let group of groupedHardcodedAreas" class="region-group">
                <h3>{{ group.region }} ({{ group.areas.length }} areas)</h3>
                <div class="area-grid">
                    <span *ngFor="let area of group.areas" class="area-chip">{{ area.name }}</span>
                </div>
            </div>
        </div>

        <!-- Jobs Queue -->
        <div class="card">
            <h2><i class="fa-solid fa-list-check"></i> Scrape Jobs Queue</h2>
            <div class="btn-row">
                <button class="run-btn" (click)="enqueueJobs()" [disabled]="enqueueing">
                    <i class="fa-solid" [class.fa-spinner]="enqueueing" [class.fa-spin]="enqueueing" [class.fa-plus]="!enqueueing"></i>
                    {{ enqueueing ? 'Creating...' : 'Enqueue Jobs' }}
                </button>
            </div>

            <div class="empty-state" *ngIf="!scrapeJobs.length">No scrape jobs yet. Add search terms above and click "Enqueue Jobs".</div>
            <div class="table-wrap" *ngIf="scrapeJobs.length">
                <table>
                    <thead>
                        <tr>
                            <th>When</th>
                            <th>Search Term</th>
                            <th>Region</th>
                            <th>Status</th>
                            <th>Cursor</th>
                            <th>Results</th>
                            <th>Created</th>
                            <th>Updated</th>
                            <th>Dupes</th>
                            <th>Cost</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let j of scrapeJobs">
                            <td>{{ runDate(j) | date: 'short' }}</td>
                            <td>{{ j.denormSearchTermName }}</td>
                            <td>{{ j.denormRegionLabel }}</td>
                            <td><span class="status-pill" [attr.data-status]="j.status">{{ jobStatusLabels[j.status] }}</span></td>
                            <td>{{ j.importCursor || 0 }}</td>
                            <td>{{ j.rawResults || 0 }}</td>
                            <td>{{ j.prospectsCreated || 0 }}</td>
                            <td>{{ j.prospectsUpdated || 0 }}</td>
                            <td>{{ j.duplicates || 0 }}</td>
                            <td>{{ j.estimatedCostUsd != null ? '$' + (j.estimatedCostUsd | number: '1.2-2') : '-' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Legacy run history -->
        <div class="card" *ngIf="runs.length">
            <h2><i class="fa-solid fa-clock-rotate-left"></i> Legacy Run History</h2>
            <div class="table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th>When</th>
                            <th>Area</th>
                            <th>Term</th>
                            <th>Status</th>
                            <th>Places</th>
                            <th>Created</th>
                            <th>Updated</th>
                            <th>Cost</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let r of runs">
                            <td>{{ runDate(r) | date: 'short' }}</td>
                            <td>{{ areaName(r.area) }}</td>
                            <td>{{ r.searchTerm }}</td>
                            <td><span class="status-pill" [attr.data-status]="r.status">{{ runStatusLabels[r.status] }}</span></td>
                            <td>{{ r.placesFound ?? '-' }}</td>
                            <td>{{ r.prospectsCreated ?? '-' }}</td>
                            <td>{{ r.prospectsUpdated ?? '-' }}</td>
                            <td>{{ r.estimatedCostUsd != null ? '$' + (r.estimatedCostUsd | number: '1.2-2') : '-' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</div>
